import { Fragment, forwardRef, useEffect, useRef, useState } from 'react';
import { ChevronDown, Mail, Search, SlidersHorizontal } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { WorkspaceStatusFilter } from '../../store/quotationWorkspaceStore';
import type { QuotationStats } from '../../types/quotation.types';

interface FilterDef {
  key: WorkspaceStatusFilter;
  label: string;
  countOf: (s: QuotationStats) => number;
}

// Order: inbox-style "Active" first, then status drill-downs, "All" at the
// bottom (separated) as the escape hatch. Archived sits at the very bottom —
// hidden by default, surfaced after All as a deliberate destination.
const FILTERS: FilterDef[] = [
  { key: 'active', label: 'Active', countOf: (s) => s.draft + s.autoDraft },
  { key: 'autodrafts', label: 'Auto-drafts', countOf: (s) => s.autoDraft },
  { key: 'drafts', label: 'Drafts', countOf: (s) => s.draft },
  { key: 'submitted', label: 'Submitted', countOf: (s) => s.submitted },
  { key: 'sent', label: 'Sent', countOf: (s) => s.sent },
  { key: 'all', label: 'All', countOf: (s) => s.total },
  { key: 'archived', label: 'Archived', countOf: (s) => s.archived },
];

interface Props {
  statusFilter: WorkspaceStatusFilter;
  onStatusChange: (next: WorkspaceStatusFilter) => void;
  quickFilter: string;
  onQuickFilterChange: (next: string) => void;
  /** Counts surfaced in the dropdown so sales sees the queue distribution
   *  without flipping through every filter. Null while still loading — the
   *  count badge simply hides until stats arrive. */
  stats: QuotationStats | null;
}

// CSC-293 redesign: single-row command bar — search input + status dropdown
// with counts. Replaces the previous 2-row layout (search + 6 wrapping pills)
// to keep the master pane vertically dense. Create button has been promoted
// out of this component to a sticky FAB inside the master pane (separates the
// "filter" intent from the "create" intent).
export const QuotationListFilters = forwardRef<HTMLInputElement, Props>(
  function QuotationListFilters(
    { statusFilter, onStatusChange, quickFilter, onQuickFilterChange, stats },
    ref,
  ) {
    const active = FILTERS.find((f) => f.key === statusFilter) ?? FILTERS[0];
    const activeCount = stats ? active.countOf(stats) : null;

    // CSC-293: keep the typed value in local state and only push it upstream
    // after the user pauses for 250ms. The previous controlled-from-store
    // pattern caused per-keystroke re-renders all the way down to the master
    // list (which kicked off a fetch + sessionStorage persist on every key).
    // The visible input stays instant; the network/store update is debounced.
    const [localValue, setLocalValue] = useState(quickFilter);

    // Mirror external resets back to local (e.g. another component clears the
    // search programmatically). Skipped while the user is mid-edit because
    // localValue's own debounce will sync up on its own.
    useEffect(() => {
      setLocalValue((prev) => (prev === quickFilter ? prev : quickFilter));
    }, [quickFilter]);

    // Stable callback ref so the debounce timer doesn't reset every render
    // when a new function reference comes in via props.
    const onChangeRef = useRef(onQuickFilterChange);
    useEffect(() => {
      onChangeRef.current = onQuickFilterChange;
    }, [onQuickFilterChange]);

    useEffect(() => {
      if (localValue === quickFilter) return;
      const timer = setTimeout(() => {
        onChangeRef.current(localValue);
      }, 250);
      return () => clearTimeout(timer);
    }, [localValue, quickFilter]);

    const unread = stats?.unreadAutoDraft ?? 0;
    const showUnreadPill = unread > 0;

    return (
      <div className="flex items-center gap-1.5 border-b bg-background px-3 py-2">
        <div className="relative flex-1 min-w-0">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={ref}
            placeholder="Search…"
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            className="h-8 pl-8 text-sm"
            aria-label="Search quotations"
          />
        </div>
        {/* CSC-297: unread AutoDraft badge. Click jumps the user straight to the
            Auto-drafts filter so they can triage. Hidden when there's nothing new. */}
        {showUnreadPill && (
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={() => onStatusChange('autodrafts')}
            className="h-8 shrink-0 gap-1 px-2 text-xs font-medium"
            title={`${unread} unread auto-draft${unread === 1 ? '' : 's'} — click to view`}
            aria-label={`${unread} unread auto-drafts`}
          >
            <Mail className="h-3.5 w-3.5" />
            <span className="tabular-nums">{unread}</span>
            <span className="hidden sm:inline">new</span>
          </Button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 shrink-0 gap-1 px-2 text-xs font-medium"
              title={`Filter: ${active.label}${activeCount ? ` (${activeCount})` : ''}`}
              aria-label={`Filter: ${active.label}`}
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              {activeCount !== null && activeCount > 0 && (
                <Badge
                  variant="secondary"
                  className="h-4 min-w-[18px] justify-center px-1 text-[10px] font-medium leading-none tabular-nums"
                >
                  {activeCount}
                </Badge>
              )}
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuLabel className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              <span>Filter</span>
              <span className="font-normal normal-case">{active.label}</span>
            </DropdownMenuLabel>
            {FILTERS.map((f) => {
              const count = stats ? f.countOf(stats) : null;
              const selected = f.key === statusFilter;
              const showSeparatorBefore = f.key === 'all';
              return (
                <Fragment key={f.key}>
                  {showSeparatorBefore && <DropdownMenuSeparator />}
                  <DropdownMenuItem
                    onSelect={() => onStatusChange(f.key)}
                    className={cn('gap-2 text-sm', selected && 'bg-accent font-medium')}
                  >
                    <span className="flex-1">{f.label}</span>
                    {count !== null && count > 0 && (
                      <span className="text-xs tabular-nums text-muted-foreground">{count}</span>
                    )}
                  </DropdownMenuItem>
                </Fragment>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  },
);

